import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { appSettings } from '../../../environments/app-settings';
import { disableErrorHandler } from '../../http/error.interceptor';
import { EntityAction } from '../../models/entity-action';
import { PageResult } from '../../models/page-result';
import { TableConfiguration } from '../../models/table-configuration';
import { StatusInfo } from '../models/status-info';
import { WorkItem } from '../models/work-item';

/**
 * Encapsulates home page (work items) related operations.
 */
@Injectable()
export class HomeService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${appSettings.api.url}ui/home`;

  /**
   * Retrieves entities status info.
   *
   * @returns Entities status info.
   */
  public getStatusInfo(): Observable<EntityAction<StatusInfo>> {
    return this.http.get<EntityAction<StatusInfo>>(`${this.baseUrl}/status`, { context: disableErrorHandler() });
  }

  /**
   * Retrieves page of work items.
   *
   * @param sortBy Name of the field for ordering.
   * @param isDescending Flag which displays if ordering should be descending.
   * @param continuationToken Next page token.
   * @returns A page of work items.
   */
  public getPage(sortBy: string, isDescending: boolean, continuationToken: string): Observable<PageResult<WorkItem>> {
    const params = new HttpParams()
      .set('sortBy', sortBy)
      .set('isDescending', isDescending.toString());

    let headers = new HttpHeaders();
    if (continuationToken)
      headers = headers.set('x-syk-continuation', continuationToken);

    return this.http.get<PageResult<WorkItem>>(this.baseUrl, { params, headers });
  }

  /**
   * Retrieves configuration data for work item table with allowed actions list.
   *
   * @returns EntityAction instance with TableConfiguration.
   */
  public getConfiguration(): Observable<EntityAction<TableConfiguration>> {
    return this.http.get<EntityAction<TableConfiguration>>(`${this.baseUrl}/configuration`);
  }
}
